import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { INSTITUTE, scrollToId, WA_DEFAULT } from "@/lib/site";

const LINKS = [
  { label: "Home", id: "#home" },
  { label: "About", id: "#about" },
  { label: "Courses", id: "#courses" },
  { label: "Student Work", id: "#student-work" },
  { label: "Why Us", id: "#why-us" },
  { label: "Contact", id: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-obsidian/85 backdrop-blur-xl border-b border-white/[0.06] py-3"
            : "bg-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <button
            onClick={() => go("#home")}
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
            aria-label="Motion Mind Institute — home"
          >
            <img
              src="/logo.jpeg"
              alt="Motion Mind Institute logo"
              className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl object-cover ring-1 ring-white/15 group-hover:ring-amber-500/60 transition-all duration-300"
            />
            <span className="text-left leading-none">
              <span className="block font-display font-bold text-white text-sm sm:text-base tracking-tight">
                Motion <span className="text-brand">Mind</span> Institute
              </span>
              <span className="block mt-1 font-mono text-[9px] uppercase tracking-[0.34em] text-gray-500">
                Learn · Create · Grow
              </span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="relative text-sm text-gray-300 hover:text-white transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-amber-500 after:transition-all after:duration-300 hover:after:w-full"
                data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => go("#contact")}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-obsidian hover:bg-amber-400 hover:shadow-glow-amber-sm hover:-translate-y-0.5 transition-all duration-300"
              data-testid="nav-enquire-button"
            >
              Enquire Now
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full glass text-white hover:border-amber-500/50 transition-colors"
              aria-label="Open menu"
              data-testid="nav-hamburger-button"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-obsidian/97 backdrop-blur-2xl flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-5">
              <span className="font-display font-bold text-white">
                Motion <span className="text-brand">Mind</span>
              </span>
              <button
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center h-11 w-11 rounded-full glass text-white"
                aria-label="Close menu"
                data-testid="mobile-menu-close-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.45 }}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-2xl sm:text-3xl font-bold text-white/85 hover:text-amber-500 py-3 border-b border-white/[0.06] transition-colors"
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                >
                  <span className="font-mono text-xs text-amber-500/70 mr-4 align-middle">0{i + 1}</span>
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="px-8 pb-10 space-y-4"
            >
              <button
                onClick={() => go("#contact")}
                className="w-full rounded-full bg-amber-500 py-4 font-semibold text-obsidian"
                data-testid="mobile-menu-enquire-button"
              >
                Enquire Now
              </button>
              <a
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center rounded-full border border-white/15 py-4 text-gray-200"
                data-testid="mobile-menu-whatsapp-link"
              >
                Chat on WhatsApp
              </a>
              <p className="pt-2 text-center font-mono text-xs text-gray-500 tracking-widest">
                {INSTITUTE.phoneDisplay} · {INSTITUTE.email}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
